import { Container, Heading, Text } from '@chakra-ui/react'

import { makeServerProps } from '@/server/query'

import makePage from '@/client/services/pages/makePage'
import * as url from '@/client/services/url'

import Layout from '@/components/Layout/Layout'
import ShwLink from '@/components/ShwLink'

const TermsAndConditions: React.FC = () => (
  <Layout title="Learners, Tutors, and Certifications Terms & Conditions">
    <Container maxWidth="768px">
      <Heading size="2xl" as="h2" my="16px">
        Learners, Tutors, and Certifications Terms & Conditions
      </Heading>
      <section>
        <Text>
          We’re excited to have you here! In joining Schoolhouse.world as a learner, we ask that you
          acknowledge the following terms & conditions.
        </Text>
      </section>
      <section>
        <ul>
          <li>You confirm that you are 13 years of age or older.</li>
          <li>
            You agree to be recorded during sessions for quality and safety purposes, and you agree
            not to share screenshots or recordings of sessions under any circumstance.
          </li>
          <li>
            You agree that you will immediately report any inappropriate behavior that occurs on
            Schoolhouse.world&apos;s pages and sessions by reaching out to User Support on the{' '}
            <ShwLink href={url.faq.getArticleUrl('how-do-i-contact-the-schoolhouse-team')}>
              Help Desk
            </ShwLink>
            .
          </li>
          <li>
            You agree to only use Schoolhouse.world, its Zoom videos, and any other forms of related
            software to communicate in a professional manner about topics related to your academic
            learning. Any inappropriate material will be grounds for immediate and permanent
            removal.
          </li>
          <li>
            You acknowledge you have read and agree to Schoolhouse.world’s{' '}
            <ShwLink href="/about/privacy">Privacy Policy</ShwLink>.
          </li>
          <li>
            By joining Schoolhouse.world, you agree to abide by its protocols and terms of service.
            The community and its affiliates (organizers, tutors, tutees, participants, and
            partners) shall not be held liable for any damages arising out of the site, its
            associated content, and its tutoring sessions.
          </li>
          <li>
            You agree that you will not copy, reproduce, display, publish or distribute any
            Schoolhouse World content outside of the platform, without Schoolhouse World&apos;s
            express prior written consent.
          </li>
        </ul>
      </section>
      <section>
        <Text mb={3}>
          We may terminate or suspend your access to Schoolhouse.world immediately, without prior
          notice or liability, for any reason whatsoever, including without limitation if you breach
          these Terms and Conditions.
        </Text>
        <Text mb={3}>
          The terms & conditions of Schoolhouse.world are subject to change. Your use of the website
          is at all times subject to our latest terms & conditions.
        </Text>
        <Text>
          If you have any questions regarding these terms, please start a chat request from our{' '}
          <ShwLink href={url.faq.getUrl()}>Help Desk</ShwLink>.
        </Text>
      </section>

      <Heading size="2xl" as="h2" my="16px">
        Tutor Terms & Conditions Overview
      </Heading>
      <section>
        <Text>
          We’re excited to have you tutor! In joining Schoolhouse.world as a tutor, we ask that you
          acknowledge the following terms & conditions.
        </Text>
      </section>
      <section>
        <ul>
          <li>You confirm that you are 13 years of age or older.</li>
          <li>You agree to be recorded during sessions for quality and safety purposes.</li>
          <li>
            You agree to follow our{' '}
            <ShwLink href={url.getAboutUrl('content-policy')}>Session Content Policy</ShwLink>.
          </li>
          <li>
            You agree that you will immediately report any inappropriate behavior that occurs on
            Schoolhouse.world&apos;s pages and sessions by reaching out to User Support on the{' '}
            <ShwLink href={url.faq.getArticleUrl('how-do-i-contact-the-schoolhouse-team')}>
              Help Desk
            </ShwLink>
            .
          </li>
          <li>
            You agree always to abide by the{' '}
            <ShwLink href="https://coda.io/@shw/tutor-community-guidelines">
              Tutor Community Guidelines
            </ShwLink>
            , including communicating in a professional manner on Schoolhouse.world, its Slack
            channels, its Zoom rooms, and any other related software. Any inappropriate material
            will be grounds for immediate and permanent removal.
          </li>
          <li>
            You acknowledge you have read and agree to Schoolhouse.world’s{' '}
            <ShwLink href="/about/volunteer-policy">Volunteer Policy</ShwLink>. This Volunteer
            Policy includes liability waivers, consents, and other legal agreements on your part.
          </li>
          <li>
            You acknowledge you have read and agree to Schoolhouse.world’s{' '}
            <ShwLink href="/about/privacy">Privacy Policy</ShwLink>.
          </li>
          <li>
            You agree to host sessions on Zoom through the Zoom account provided, and you have the
            technology required to do so.
          </li>
          <li>
            You agree to show up for every tutoring session that you sign up for. If you are unable
            to attend a session, you will inform Schoolhouse.world immediately.
          </li>
          <li>
            You agree to be respectful, inclusive, and encouraging to all students in your tutoring
            sessions.
          </li>
          <li>
            You understand that Schoolhouse.world may collect feedback from students about each of
            your tutoring sessions, including numerical ratings and comments on session quality. We
            use this feedback to evaluate tutors and improve our programs. If your tutoring session
            was affiliated with a Schoolhouse.world partnership, we may share this feedback with the
            partner.
          </li>
          <li>
            You agree that you are not an employee of Schoolhouse.world and will not be paid. You
            have freely and voluntarily agreed to participate in certain activities of
            Schoolhouse.world as a volunteer.
          </li>
          <li>
            You agree that you will not copy, reproduce, display, publish or distribute any
            Schoolhouse World content outside of the platform, without Schoolhouse World&apos;s
            express prior written consent.
          </li>
        </ul>
      </section>
      <section>
        <Text mb={3}>
          We may terminate or suspend your access to Schoolhouse.world immediately, without prior
          notice or liability, for any reason whatsoever, including without limitation if you breach
          these Terms and Conditions.
        </Text>
        <Text mb={3}>
          The terms & conditions of Schoolhouse.world are subject to change. Your use of the website
          is at all times subject to our latest terms & conditions.
        </Text>
        <Text>
          If you have any questions regarding these terms, please start a chat request from our{' '}
          <ShwLink href={url.faq.getUrl()}>Help Desk</ShwLink>.
        </Text>
      </section>

      <Heading size="2xl" as="h2" my="16px">
        Certification Terms & Conditions Overview
      </Heading>

      <Heading size="xl" as="h3" mb="16px">
        Section 1. Prohibited Items for Recording
      </Heading>
      <section>
        <Text>These items may not be in the room while recording</Text>
        <ul>
          <li>Headphones/Earbuds</li>
          <li>Any electronics besides your computer/recording device</li>
          <li>Sunglasses, or anything that covers the face</li>
          <li>
            You may only use a calculator when explicitly permitted in the test question. If the
            question requires a graphing calculator and you do not have one,{' '}
            <ShwLink href="https://www.desmos.com/calculator">Desmos</ShwLink> can be used instead.
          </li>
        </ul>
      </section>

      <Heading size="xl" as="h3" mb="16px">
        Section 2. Procedure While Recording
      </Heading>
      <section>
        <ul>
          <li>Please record your whole screen.</li>
          <li>
            You may only have the following tabs open: Schoolhouse.world, Khan Academy, and Desmos
            (only for use when a question allows a graphing calculator). All other tabs are
            prohibited.
          </li>
          <li>
            You may not have any other applications open except your internet browser. If you want
            to show your work on the screen, please use Khan Academy&apos;s built-in
            drawing/annotation tool.
          </li>
          <li>
            The recording must be one continuous video. Our recording system does not allow for
            pausing and resuming the video.
          </li>
          <li>
            Your camera must be on and show your whole face at all times during the recording.
          </li>
          <li>Your microphone must be on during the whole video.</li>
          <li>
            If we are aware of another device being in the room while recording, your video will be
            disqualified.
          </li>
        </ul>
      </section>

      <Heading size="xl" as="h3" mb="16px">
        Section 3. Repercussions
      </Heading>
      <section>
        <Text>These items may not be in the room while recording</Text>
        <ul>
          <li>
            If we detect suspicious activity, our review board will conduct an investigation and
            decide on the appropriate action from the following possibilities:
            <ul>
              <li>
                <u>Rejection</u> of video with the opportunity to resubmit
              </li>
              <li>
                <u>Live questioning</u> to validate the applicants content knowledge.
              </li>
              <li>
                <u>Blocklisting</u> from our system, along with invalidation of all certifications
                earned. Blocklisting incidents will be reported to organizations which inquire about
                your score.
              </li>
            </ul>
          </li>
          <li>
            Schoolhouse.world reserves the right to invalidate a student’s certification if proof of
            cheating is uncovered at any time.
          </li>
        </ul>
      </section>

      <Heading size="xl" as="h3" mb="16px">
        Section 4: Privacy
      </Heading>
      <section>
        <Text>
          View our privacy policy <ShwLink href="/about/privacy">here</ShwLink>.
        </Text>
        <br />
        <Text>
          Please contact us if you have any concerns by reaching out to User Support on the{' '}
          <ShwLink href={url.faq.getArticleUrl('how-do-i-contact-the-schoolhouse-team')}>
            Help Desk
          </ShwLink>
          .
        </Text>
        <br />
        <Text color="fg.error">
          Failure to abide by these terms and conditions may result in being blocklisted from the
          platform. The consequences of attempting to cheat on a Schoolhouse.world certification are
          serious and irreversible.
        </Text>
      </section>
    </Container>
  </Layout>
)

const TermsAndConditionsPage = makePage(TermsAndConditions, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(TermsAndConditionsPage)
export default TermsAndConditionsPage
